<template>
  <form class="mb-2">
    <TextQuestion
      data-private
      :tid="`${tid}-textarea-5d`"
      :tourId="`${tid}-textArea-correctiveAction`"
      :maxLength="$constants.problemSolving.correctiveActionLength"
      :value="correctiveAction"
      :isEditMode="isEditMode"
      :placeholder="$t('problem_solving.improve.corrective_action_placeholder')"
      :question="`${$t('problem_solving.improve.corrective_action_question')}*`"
      :error="markRequiredFields && !correctiveAction"
      rows="1"
      @input="handleRootCauseChange('correctiveAction', $event)"
      @focusOut="trackEvent('correctiveAction')"
    />

    <div
      class="d-flex flex-column align-start"
      :class="{ 'mt-0 mb-4': isEditMode, 'my-4': !isEditMode }"
    >
      <FieldLabel
        v-if="!isEditMode"
        :text="$t('problem_solving.improve.corrective_action_assignee_trigger')"
      />
      <div v-if="isEditMode" class="mt-2 pa-0" style="width:100%">
        <AssigneesSelector
          localSearch
          multiple
          :availableLocalAccounts="accounts"
          :selectedAccountIds="selectedMember"
          :label="
            $t('problem_solving.improve.corrective_action_assignee_trigger')
          "
          @change="selectTeamMember"
        />
      </div>
      <div v-else-if="showAssigneeChip">
        <ChipAvatar
          class="mt-2 me-1"
          small
          v-for="assignee in correctiveActionAssignees"
          :account="assigneeChip(assignee)"
          :key="assignee.name"
        />
      </div>
      <div v-else>
        <v-chip disabled small class="mt-2">{{
          $t('common.in_progress_status')
        }}</v-chip>
      </div>
    </div>

    <section v-if="isEditMode || correctiveActionMedia.length > 0">
      <FieldLabel :text="$t('problem_solving.control.evidence_title')" />
      <MediaContainer
        data-testid="file-evidence"
        onlyThumbnails
        smallThumbnails
        :data-tourid="`${tid}-photoSelector-evidence`"
        :images="correctiveActionMedia"
        :readOnly="!isEditMode || isSaving"
        @change="handleCorrectiveActionMediaChange"
      />
    </section>

    <h6 class="mt-8 mb-0 pa-0 h6">
      {{ $t('problem_solving.phases.control') }}
    </h6>
    <p
      class="control__description mt-4 mb-6 font-size-12 grey--text text--darken-1"
    >
      {{ $t('problem_solving.control.description') }}
    </p>

    <BooleanQuestion
      data-private
      name="correctiveActionWorked"
      data-testid="radio-6d"
      :id="`correctiveActionWorked_${rootCause.position + 1}`"
      :tid="tid"
      :value="correctiveActionWorked"
      :isEditMode="isEditMode"
      :error="markRequiredFields && !correctiveLearning && !preventiveAction"
      :noText="
        $t('problem_solving.control.corrective_action_did_not_worked_option')
      "
      :question="
        `${$t('problem_solving.control.corrective_action_result_question')}*`
      "
      :yesText="$t('problem_solving.control.corrective_action_worked_option')"
      @input="handleCorrectiveActionWorkedChange"
    />
    <TextQuestion
      v-if="shouldShowPreventiveAction"
      class="mt-4"
      data-private
      data-testid="input-7d"
      rows="2"
      :tid="`${tid}-textarea-7d`"
      :tourId="`${tid}-textArea-preventiveAction`"
      :maxLength="this.$constants.problemSolving.maxLengthPreventiveAction"
      :value="preventiveAction"
      :isEditMode="isEditMode"
      :error="markRequiredFields && !preventiveAction"
      :placeholder="$t('problem_solving.control.preventive_action_placeholder')"
      :question="`${$t('problem_solving.control.preventive_action_question')}*`"
      @input="handleRootCauseChange('preventiveAction', $event)"
      @focusOut="trackEvent('preventiveAction')"
    />
    <TextQuestion
      v-if="shouldShowCorrectiveLearning"
      class="mt-4"
      data-private
      data-testid="input-7d"
      rows="4"
      :value="correctiveLearning"
      :isEditMode="isEditMode"
      :error="markRequiredFields && !correctiveLearning"
      :maxLength="this.$constants.problemSolving.maxLengthPreventiveAction"
      :placeholder="$t('problem_solving.control.learning_placeholder')"
      :question="`${$t('problem_solving.control.learning_question')}*`"
      @input="handleRootCauseChange('correctiveLearning', $event)"
    />
  </form>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'

import FieldLabel from '@/components/Core/FieldLabel'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import BooleanQuestion from '@/components/Rev/BooleanQuestion.vue'
import AssigneesSelector from '@/components/AssigneesSelector/AssigneesSelector.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'

const AUTHOR_ROLE = 'author'
const COACH_ROLE = 'coach'
const COLLABORATOR_ROLE = 'collaborator'

export default {
  name: 'SolutionForm',

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    rootCause: {
      type: Object,
      required: true,
    },
    tid: {
      type: String,
      required: true,
    },
    isSaving: Boolean,
    markRequiredFields: Boolean,
  },

  components: {
    TextQuestion,
    BooleanQuestion,
    MediaContainer,
    FieldLabel,
    AssigneesSelector,
    ChipAvatar,
  },

  data() {
    return {
      searchTerm: '',
      accounts: [],
      cachedAccounts: [],
      selectedMember: this.correctiveActionAssigneesIds() || [],
    }
  },

  mounted() {
    this.setAccounts()
  },

  watch: {
    'rev.team': {
      immediate: true,
      deep: true,
      handler() {
        this.setAccounts()
      },
    },
  },

  computed: {
    ...mapState('problemSolving', {
      rev: ({ rev }) => rev,
    }),

    ...mapGetters(['organizationId']),

    correctiveAction() {
      return this.rootCause?.correctiveAction ?? ''
    },

    correctiveActionWorked() {
      return this.rootCause?.correctiveActionWorked ?? null
    },

    preventiveAction() {
      return this.rootCause?.preventiveAction ?? ''
    },

    correctiveLearning() {
      return this.rootCause?.correctiveLearning ?? ''
    },

    correctiveActionMedia() {
      return this.rootCause?.correctiveActionMedia ?? []
    },

    team() {
      return [
        this.rev?.team?.owner,
        this.rev?.team?.coach,
        ...(this.rev?.team?.collaborators ?? []),
      ].filter(account => Boolean(account?.id))
    },

    correctiveActionAssignees() {
      return this.rootCause?.correctiveActionAssignees ?? []
    },

    location() {
      return {
        site: this.rev?.location?.site?._id ?? this.rev?.location?.site,
      }
    },

    showAssigneeChip() {
      return !_.isEmpty(this.correctiveActionAssignees) && !this.isEditMode
    },

    shouldShowPreventiveAction() {
      return (
        !_.isNull(this.correctiveActionWorked) && this.correctiveActionWorked
      )
    },
    shouldShowCorrectiveLearning() {
      return (
        !_.isNull(this.correctiveActionWorked) && !this.correctiveActionWorked
      )
    },
  },

  methods: {
    handleRootCauseChange(field, value) {
      this.$emit('rootCauseChange', {
        ...this.rootCause,
        [field]: value,
      })
    },

    handleCorrectiveActionWorkedChange(correctiveActionWorked) {
      this.trackEvent('correctiveActionWorked')

      this.$emit('rootCauseChange', {
        ...this.rootCause,
        correctiveActionWorked,
        preventiveAction: correctiveActionWorked
          ? this.rootCause.preventiveAction
          : '',
        correctiveLearning: !correctiveActionWorked
          ? this.rootCause.correctiveLearning
          : '',
      })
    },

    handleCorrectiveActionMediaChange(media) {
      this.trackEvent('evidencePhotos')
      this.handleRootCauseChange('correctiveActionMedia', media)
    },

    handleCorrectiveActionImageRemove() {
      this.handleRootCauseChange('correctiveActionMedia', [])
    },

    trackEvent(changedField) {
      this.$emit('trackEvent', changedField)
    },

    selectTeamMember(correctiveActionAssigneesIds) {
      const correctiveActionAssignees = this.accounts.filter(member =>
        correctiveActionAssigneesIds.includes(member.id)
      )
      this.$emit('rootCauseChange', {
        ...this.rootCause,
        correctiveActionAssignees,
      })
      this.selectedMember = correctiveActionAssigneesIds
    },

    setAccounts() {
      const collaborators = this.rev?.team?.collaborators ?? []
      const accounts = _.compact([
        {
          ...this.rev?.team?.owner,
          displayname: this.rev?.team?.owner?.displayName ?? '',
          familyname: this.rev?.team?.owner?.familyName ?? '',
          position: this.$t(`common.roles.${AUTHOR_ROLE}`),
        },
        this.rev.team.coach?.id
          ? {
              ...this.rev.team.coach,
              displayname: this.rev?.team?.coach?.displayName ?? '',
              familyname: this.rev?.team?.coach?.familyName ?? '',
              position: this.$t(`common.roles.${COACH_ROLE}_one`),
            }
          : null,
        ...collaborators.map(collaborator => {
          return {
            ...collaborator,
            displayname: collaborator?.displayName ?? '',
            familyname: collaborator?.familyName ?? '',
            position: this.$t(`common.roles.${COLLABORATOR_ROLE}_one`),
          }
        }),
      ])

      this.accounts = accounts
      this.cachedAccounts = accounts
    },

    assigneeChip(assignee) {
      return {
        ...assignee,
        displayname: assignee?.displayName ?? '',
        familyname: assignee?.familyName ?? '',
      }
    },

    correctiveActionAssigneesIds() {
      return this.rootCause?.correctiveActionAssignees?.map(member => member.id)
    },
  },
}
</script>

<style lang="scss" scoped>
.control__description {
  max-width: 460px;
  line-height: 16px;
}

.assignees-widget {
  &__empty-avatar-container {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: $color-blue-grey-lighten3;

    i {
      color: $color-blue-grey-darken3;
    }
  }
}
</style>
